import type { Database } from "@pex/database/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { BillingApp, BillingProcessor, PaymentEvent } from "../types";

function sharedSchema(client: SupabaseClient<Database>) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema("shared");
}

function events(client: SupabaseClient<Database>) {
  return sharedSchema(client).from("payment_events");
}

function rowToPaymentEvent(row: Record<string, unknown>): PaymentEvent {
  return {
    id: row.id as string,
    subscription_id: (row.subscription_id as string | null) ?? null,
    organization_id: (row.organization_id as string | null) ?? null,
    processor: row.processor as BillingProcessor,
    processor_event_id: row.processor_event_id as string,
    event_type: row.event_type as string,
    amount: (row.amount as number | null) ?? null,
    currency: (row.currency as string | null) ?? null,
    processor_fee: (row.processor_fee as number | null) ?? null,
    status: (row.status as string | null) ?? null,
    paid_at: (row.paid_at as string | null) ?? null,
    event_at: row.event_at as string,
    metadata: (row.metadata as Record<string, unknown>) ?? {},
    created_at: row.created_at as string,
  };
}

export interface InsertPaymentEventInput {
  amount?: number | null;
  currency: string | null;
  event_at: string;
  event_type: string;
  metadata?: Record<string, unknown>;
  organization_id?: string | null;
  paid_at?: string | null;
  processor: BillingProcessor;
  processor_event_id: string;
  processor_fee?: number | null;
  status?: string | null;
  subscription_id?: string | null;
}

export interface InsertPaymentEventResult {
  duplicate: boolean;
  event?: PaymentEvent;
  inserted: boolean;
}

export async function insertPaymentEvent(
  client: SupabaseClient<Database>,
  input: InsertPaymentEventInput
): Promise<InsertPaymentEventResult> {
  const { data, error } = await events(client)
    .insert({
      subscription_id: input.subscription_id ?? null,
      organization_id: input.organization_id ?? null,
      processor: input.processor,
      processor_event_id: input.processor_event_id,
      event_type: input.event_type,
      amount: input.amount ?? null,
      currency: input.currency,
      processor_fee: input.processor_fee ?? null,
      status: input.status ?? null,
      paid_at: input.paid_at ?? null,
      event_at: input.event_at,
      metadata: input.metadata ?? {},
    })
    .select()
    .single();

  if (error) {
    // Unique constraint violation on (processor, processor_event_id) — idempotent duplicate
    if (error.code === "23505") {
      return { inserted: false, duplicate: true };
    }
    throw new Error(`Failed to insert payment event: ${error.message}`);
  }

  return {
    inserted: true,
    duplicate: false,
    event: rowToPaymentEvent(data as Record<string, unknown>),
  };
}

/**
 * Patches the subscription_id on a payment_events row that was inserted before
 * the subscription existed (i.e. the initial subscription.create event for a
 * new subscriber). Silently no-ops if the row already has a subscription_id.
 */
export async function updatePaymentEventSubscriptionId(
  client: SupabaseClient<Database>,
  eventId: string,
  subscriptionId: string
): Promise<void> {
  const { error } = await events(client)
    .update({ subscription_id: subscriptionId })
    .eq("id", eventId)
    .is("subscription_id", null);

  if (error) {
    console.warn(
      "[billing] Failed to backfill subscription_id on payment_events row",
      { eventId, subscriptionId, error: error.message }
    );
  }
}

/**
 * True when this subscription already has an event of `eventType` other than
 * `excludeEventId`.
 *
 * Backs the Phase 7 email routing rules that span *two different events*, which
 * the per-event duplicate guard can't cover (handoff §3/§6):
 * - **P2 first-charge suppression (E6):** at signup, charge.success fires
 *   seconds after subscription.create and P1 already confirmed the
 *   subscription, so P2 must not also send on that first charge.
 * - **S2 single-fire:** a voluntary cancellation emits not_renew *then*
 *   disable; staff get one S2, sent from disable only when no not_renew ran.
 *
 * Subscription-scoped rather than org-scoped on purpose — a brand-new
 * subscription is still pending (organization_id null) here, so
 * getPaymentHistory can't answer this.
 *
 * The caller inserts the current event before sending, so it is excluded by id.
 */
export async function hasPriorEventOfType(
  client: SupabaseClient<Database>,
  subscriptionId: string,
  eventType: string,
  excludeEventId: string
): Promise<boolean> {
  const { data, error } = await events(client)
    .select("id")
    .eq("subscription_id", subscriptionId)
    .eq("event_type", eventType)
    .neq("id", excludeEventId)
    .limit(1);

  if (error) {
    throw new Error(
      `Failed to check for prior ${eventType} event: ${error.message}`
    );
  }

  return (data?.length ?? 0) > 0;
}

export interface GetPaymentHistoryOptions {
  app?: BillingApp;
  limit?: number;
}

export async function getPaymentHistory(
  client: SupabaseClient<Database>,
  organizationId: string,
  options: GetPaymentHistoryOptions = {}
): Promise<PaymentEvent[]> {
  const { app, limit = 50 } = options;

  let query = events(client)
    .select("*")
    .eq("organization_id", organizationId)
    .not("subscription_id", "is", null);

  const ALLOWED_APPS = ["bridge", "pulse", "circle", "habitat"] as const;
  if (app && !ALLOWED_APPS.includes(app as never)) {
    throw new Error(`Unknown app: ${app}`);
  }
  // NOTE: no webhook handler currently writes `app` into payment_events.metadata
  // (that field is on subscriptions). The IS NULL branch always matches, so this
  // filter is a no-op today — it exists as a forward hook for future multi-app
  // scoping. The ->> operator is needed for correct text comparison when this lands.
  if (app) {
    query = query.or(`metadata->>app.is.null,metadata->>app.eq.${app}`);
  }

  const { data, error } = await query
    .order("event_at", { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(`Failed to fetch payment history: ${error.message}`);
  }

  return (data ?? []).map((row: Record<string, unknown>) =>
    rowToPaymentEvent(row)
  );
}

export async function getMemberCharges(
  client: SupabaseClient<Database>,
  organizationId: string,
  limit = 50
): Promise<PaymentEvent[]> {
  const { data, error } = await events(client)
    .select("*")
    .eq("organization_id", organizationId)
    .eq("event_type", "charge.success")
    .is("subscription_id", null)
    .filter("metadata->metadata->>type", "eq", "member_activation")
    .order("event_at", { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(`Failed to fetch member charges: ${error.message}`);
  }

  return (data ?? []).map((row: Record<string, unknown>) =>
    rowToPaymentEvent(row)
  );
}
